import {
  Alert,
  AlertColor,
  Box,
  Button,
  Drawer,
  Grid,
  Snackbar,
} from '@mui/material'
import { DelegatePermission, User } from 'api/models/User.type'
import confirmationAlert from 'components/Common/ConfirmAlert/ConfirmAlert'
import SectionHeader from 'components/Common/SectionHeader'
import TypeScriptTable from 'components/Common/Tables/TypeScriptTable/TypeScriptTable'
import { getDelegateColumnsCA } from 'components/User/DelegateTables/DelegateTableColumns'
import UpdateUserDelegate from 'components/User/UpdateUser/UpdateUserDelegate'
import useClientUsers from 'hooks/useClientUsers'
import useDebounce from 'hooks/useDebounce'
import React, { useState } from 'react'
import { updateUser } from 'reducers/usersReducer'

type DelegatesProps = {
  client: any
}

const pageHeader = 'Delegates'
const pageInfo = 'Delegates are users who can manage the client account.'

const Delegates = ({ client }: DelegatesProps) => {
  const [openDelegateDrawer, setOpenDelegateDrawer] = React.useState(false)
  const [showAlert, setShowAlert] = useState(false)
  const [alertType, setAlertType] = useState<AlertColor>()
  const [alertMessage, setAlertMessage] = useState('')

  //get the client Users
  const { users: clientUsers, refetch } = client.id
    ? useClientUsers(client.id)
    : { users: [] }
  console.log('clientUsers', clientUsers)

  const userDelegates = clientUsers?.filter(
    (user: any) => user.delegates && user.delegates.length > 0,
  )
  console.log('userDelegates', userDelegates)

  const deleteDelegateFromUser = async (userId: number, delegateId: number) => {
    var existingUser = clientUsers?.find((user: any) => user.id === userId)

    var existingDelegates = existingUser?.delegates

    var removedDelegates = existingDelegates?.filter(
      (delegate: DelegatePermission) =>
        delegate.delegate.delegateId !== delegateId,
    )

    const updatedDelegates = removedDelegates.map((delegate: any) => ({
      permissionTypeName: delegate.permissionTypeName,
      delegateId: delegate.delegate.delegateId,
      startDateTime: delegate.delegate.startDateTime,
      endDateTime: delegate.delegate.endDateTime,
    }))

    confirmationAlert({
      title: 'Remove Delegate',
      action: async function (): Promise<void> {
        try {
          if (updatedDelegates) {
            await updateUser({
              updatedUser: {
                delegates: [...updatedDelegates],
              },
              userid: userId,
            })
          } else {
            throw new Error('User profile not found.')
          }
          setShowAlert(true)
          setAlertType('success')
          setAlertMessage('Delegate removed from user')
          refetch()
        } catch (error) {
          setShowAlert(true)
          setAlertType('error')
          setAlertMessage(`No updates made: ${error.message}`)

          console.error(
            `Error removing delegate with ID ${delegateId} from user ${userId}:`,
            error,
          )
        }
        refetch()
      },
      confirmationMessage: 'The action will remove the delegate from the user.',
      confirmationQuestion: 'Are you sure you want to proceed?',
    })
  }

  const expandedRows = (rows: User[]) => {
    return rows?.flatMap((row) => {
      const delegateRows = row?.delegates?.map(
        (delegate: DelegatePermission) => ({
          ...row,
          delegatesFlat: {
            permissionTypeName: delegate.permissionTypeName,
            delegateFirstName: delegate.delegate.firstName,
            delegateLastName: delegate.delegate.lastName,
            delegateEmail: delegate.delegate.email,
            delegateId: delegate.delegate.delegateId,
            delegateStartDateTime: delegate.startDateTime,
            delegateEndDateTime: delegate.endDateTime,
            removeDelegate: deleteDelegateFromUser,
          },
        }),
      )
      return [row, ...delegateRows]
    })
  }

  console.log('expandedRows', expandedRows(userDelegates))
  const filteredExpandedRows = expandedRows(userDelegates)?.filter(
    (row) => row.delegatesFlat,
  )

  return (
    <Box component={'div'}>
      <Grid container>
        <Grid item xs={12} sx={{ marginTop: '20px' }}>
          <SectionHeader
            title={`${pageHeader} for ${client.companyName}`}
            subtitle={pageInfo}
          />
        </Grid>
        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenDelegateDrawer(true)}
          >
            Add Delegate
          </Button>
        </Grid>
        <Grid item xs={8} sx={{ marginTop: '40px' }}>
          <TypeScriptTable
            data={filteredExpandedRows}
            columns={getDelegateColumnsCA().userTableColumns}
          />
        </Grid>
        <Drawer
          anchor={'right'}
          open={useDebounce(openDelegateDrawer, 500)}
          onClose={() => {
            setOpenDelegateDrawer(false)
          }}
          PaperProps={{ sx: { width: '500px' } }}
        >
          <UpdateUserDelegate clientUsers={clientUsers} />
        </Drawer>

        <Snackbar
          open={showAlert}
          autoHideDuration={6000}
          onClose={() => setShowAlert(false)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <Alert
            onClose={() => setShowAlert(false)}
            severity={alertType}
            sx={{ width: '100%' }}
          >
            {alertMessage}
          </Alert>
        </Snackbar>
      </Grid>
    </Box>
  )
}
export default Delegates
